<!-- PrintNotes.vue -->
<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTextStore } from "@/stores/useTextStore";
import { exportNotesPdf } from "@/services/noteService";
import { storeToRefs } from "pinia";
import { useDraggableModal } from "@/composables/useDraggableModal";

// Quem abre o modal é o menu ⋯ da área de leitura (TextDisplayer.vue): este
// componente é só o modal e a exportação.
const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);

const noteStore = useNoteStore();
const textStore = useTextStore();

const { isExponentMode, isPageMarksMode } = storeToRefs(noteStore);

// ── Opções de impressão ──────────────────────────────────────────────────────
// Tudo nasce marcado: o modal diz o que vai no PDF, e o usuário desmarca o que
// não quer. O texto, o card do texto e a numeração das páginas não estão aqui
// porque saem sempre.
const options = reactive({
  activityMetadata: true,
  relatedNotes: true,
  detachedNotes: true,
  textMarks: true,
  pageHeader: true,
});

const optionList = [
  {
    key: "activityMetadata",
    label: "Metadados de atividades",
    hint: "Título, andamento, contagem de notas, início e fim",
  },
  {
    key: "relatedNotes",
    label: "Notas relacionadas",
    hint: "Notas ligadas a trechos, na ordem do texto",
  },
  {
    key: "detachedNotes",
    label: "Notas avulsas",
    hint: "Notas sem vínculo com trechos do texto",
  },
  {
    key: "textMarks",
    label: "Texto com as marcações",
    hint: "Desmarcado, o texto sai sem marcação e sem o card delas",
  },
  {
    key: "pageHeader",
    label: "Cabeçalho em todas as páginas",
    hint: "Desmarcado, o cabeçalho sai só na primeira página",
  },
];

const isExporting = ref(false);

// O painel é arrastável pelo cabeçalho, como nos outros modais.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

// O painel não desmonta entre aberturas: recentraliza a cada abertura, como
// manda o composable.
watch(
  () => props.show,
  (open) => {
    if (open) resetPosition();
  }
);

function closeOptions() {
  if (isExporting.value) return;
  emit("close");
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape") closeOptions();
}

// ── Toast ─────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("success"); // "success" | "error"
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ─────────────────────────────────────────────────────────────────────────────

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

async function handleExportClick() {
  if (isExporting.value) return;

  const exportMode = "full-text-with-notes-at-end";
  const textId = textStore.selectedText?.textId;
  const textTitle = textStore.selectedText?.title || "Documento";

  if (!textId) {
    showErrorToast("ID do texto não encontrado.");
    return;
  }

  const noteIds = noteStore.notesForHighlight.map((note) => note.noteId);

  const payload = {
    noteIds,
    exportMode,
    hasExponent: isExponentMode.value,
    // Botão de fólio do leitor: o PDF mostra a página do original como a tela.
    hasPageMarks: isPageMarksMode.value,
    // Opções do modal. Quem decide as marcações do PDF é o modal, não o botão
    // de realce do leitor: o texto sai sempre, marcado ou não.
    hasHighlights: options.textMarks,
    // "Notas avulsas" é o antigo includeGlobalNotes: as notas sem trecho são
    // exatamente as globais.
    includeGlobalNotes: options.detachedNotes,
    hasActivityMetadata: options.activityMetadata,
    hasRelatedNotes: options.relatedNotes,
    hasPageHeader: options.pageHeader,
  };

  isExporting.value = true;

  try {
    const response = await exportNotesPdf(textId, payload);
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${textTitle}_${exportMode}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    emit("close");
    showSuccessToast("PDF com texto e notas exportado com sucesso.");
  } catch (error) {
    console.error("Erro ao exportar PDF:", error);
    showErrorToast("Houve um erro ao tentar exportar as notas.");
  } finally {
    isExporting.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-backdrop" @click.self="closeOptions">
      <div ref="panelRef" class="modal-panel small" :style="panelStyle">
        <div
          class="modal-header"
          :class="{
            'modal-header--draggable': isDraggable,
            'modal-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="modal-title">Opções de impressão</h3>

          <button
            @click="closeOptions"
            type="button"
            class="modal-close-btn"
            :disabled="isExporting"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <p class="options-intro">O que deve constar no PDF:</p>

          <div class="options-list">
            <label
              v-for="option in optionList"
              :key="option.key"
              class="option-row"
            >
              <input
                type="checkbox"
                class="checkbox-style"
                v-model="options[option.key]"
                :disabled="isExporting"
              />
              <span class="option-text">
                <span class="option-label">{{ option.label }}</span>
                <span class="option-hint">{{ option.hint }}</span>
              </span>
            </label>
          </div>

          <p class="options-note">
            O texto, o card do texto e a numeração das páginas saem sempre.
          </p>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeOptions"
            :disabled="isExporting"
          >
            Cancelar
          </button>

          <button
            type="button"
            class="btn btn-primary"
            @click="handleExportClick"
            :disabled="isExporting"
          >
            <span
              v-if="isExporting"
              class="mr-2 inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            />
            <i v-else class="fas fa-download export-icon"></i>
            {{ isExporting ? "Gerando PDF..." : "Exportar PDF" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[14000] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
            'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao exportar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel.small {
  max-width: 30rem;
}

.export-icon {
  margin-right: 0.4rem;
}

.options-intro {
  font-size: var(--text-md);
  color: var(--text-light);
  margin: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.option-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  cursor: pointer;
}

.option-row .checkbox-style {
  margin-top: 0.2rem;
}

.option-text {
  display: flex;
  flex-direction: column;
  line-height: var(--leading-snug);
}

.option-label {
  font-size: 0.9rem;
  color: var(--text-dark);
}

.option-hint {
  font-size: var(--text-sm);
  color: var(--text-subtle);
}

.options-note {
  font-size: var(--text-sm);
  color: var(--text-subtle);
  border-top: 1px solid var(--border-color);
  padding-top: 0.6rem;
  margin: 0.2rem 0 0 0;
}
</style>
